import { Icon } from '@iconify/react'
import { useEffect, useRef, useState } from 'react'
import { useApi } from '../hooks/useApi'
import { Body, Box, Dialog, Divider, Dot, ErrorText, Footer, Header, Inputs, Overlay, PinInput } from '../styles/ConfirmPinModalStyle'

interface Props {
   open: boolean
   onClose: () => void
   onConfirm: () => void
}

export default function ConfirmPinModal({ open, onClose, onConfirm }: Props) {
   const [vals, setVals] = useState(['', '', '', ''])
   const [active, setActive] = useState(0)
   const [error, setError] = useState('')
   const [loading, setLoading] = useState(false)
   const { auth } = useApi()
   const r0 = useRef<HTMLInputElement>(null)
   const r1 = useRef<HTMLInputElement>(null)
   const r2 = useRef<HTMLInputElement>(null)
   const r3 = useRef<HTMLInputElement>(null)

   useEffect(() => {
      if (open) {
         setVals(['', '', '', ''])
         setActive(0)
         setError('')
         setTimeout(() => r0.current?.focus(), 10)
      }
   }, [open])

   if (!open) return null

   async function submit(v: string[]) {
      if (v.every(d => d !== '')) {
         setLoading(true)
         const pin = v.join('')

         try {
            const res = await auth.verifyPin({ pin })
            if (res.status === 'success') {
               onClose()
               onConfirm()
            } else {
               setError(res.message)
               setVals(['', '', '', ''])
               setActive(0)
               r0.current?.focus()
            }
         } catch (error) {
            setError(error instanceof Error ? error.message : 'An error occurred')
            setVals(['', '', '', ''])
            setActive(0)
            r0.current?.focus()
         } finally {
            setLoading(false)
         }
      }
   }

   function handleInput(i: number, ch: string) {
      setError('')
      const d = ch.replace(/\D/g, '').slice(-1)
      if (!d) return

      const v = [...vals]
      v[i] = d
      setVals(v)

      if (i < 3) {
         setActive(i + 1)
            ;[r0, r1, r2, r3][i + 1]?.current?.focus()
      }
   }

   function handleKey(i: number, e: React.KeyboardEvent<HTMLInputElement>) {
      if (e.key === 'Backspace') {
         const v = [...vals]
         if (v[i]) {
            v[i] = ''
            setVals(v)
         } else if (i > 0) {
            setActive(i - 1)
               ;[r0, r1, r2, r3][i - 1]?.current?.focus()
            const vv = [...vals]
            vv[i - 1] = ''
            setVals(vv)
         }
         e.preventDefault()
      }
   }

   return (
      <Overlay>
         <Dialog onClick={e => e.stopPropagation()} role='dialog' aria-modal='true' aria-labelledby='pin-title'>
            <Header>
               <h2 id='pin-title'>Enter pin</h2>
               <button aria-label='Close' onClick={onClose}><Icon icon="material-symbols:close" width={20} /></button>
            </Header>
            <Body>
               <span className='wrap-break-word'>For security, please enter admin 4-digit pin to continue</span>
               <Inputs>
                  {vals.map((val, i) => (
                     <Box key={i} $active={active === i} $error={!!error} onClick={() => { setActive(i);[r0, r1, r2, r3][i]?.current?.focus() }}>
                        <PinInput
                           ref={[r0, r1, r2, r3][i]}
                           value={val}
                           onChange={e => handleInput(i, e.target.value)}
                           onKeyDown={e => handleKey(i, e)}
                           inputMode='numeric'
                           maxLength={1}
                           aria-label={`Digit ${i + 1}`}
                        />
                        {val ? <Dot /> : <Dot style={{ visibility: 'hidden' }} />}
                     </Box>
                  ))}
               </Inputs>
               {error && <ErrorText>{error}</ErrorText>}
            </Body>
            <Divider />
            <Footer>
               <button className='cancel' onClick={onClose} disabled={loading}>Cancel</button>
               <button className={`confirm ${vals.every(v => v) !== false ? 'enabled' : ''}`} onClick={() => submit(vals)} disabled={loading}>
                  {loading ? <Icon icon="svg-spinners:ring-resize" width={16} /> : 'Confirm'}
               </button>
            </Footer>
         </Dialog>
      </Overlay>
   )
}
